import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import geobuf from 'geobuf';
import Pbf from 'pbf';
import { Layer, Source } from 'react-map-gl/mapbox';
import { useMediaQuery } from 'react-responsive';
import { preferredTemperatureUnit } from '../../../utils';
import { brandingConfig } from '../../../config';

const s3UrlBase = 'https://seagull-visualization-layers.s3.us-east-2.amazonaws.com';

const SurfaceWaterTemperature = (props) => {
  const {
    UTCPath,
    selectedLake,
    isSharing,
  } = props;

  const isMobile = useMediaQuery({ maxWidth: 767 }) && !isSharing;
  const [isLoading, setIsLoading] = useState(false);
  const [tempLayers, setTempLayers] = useState([]);

  const fetchOfs = async (ofs) => {
    const ofsUrl = `${s3UrlBase}${UTCPath}/${ofs}-temp.pbf`;
    const ofsResponse = await fetch(ofsUrl);
    const ofsData = await ofsResponse.arrayBuffer();
    return geobuf.decode(new Pbf(ofsData));
  };

  // TODO: use useQuery here so it's not refetching when re-rendering
  const fetchData = async (selectedLake) => {
    if (selectedLake.length > 0) {
      const ofs = `l${selectedLake[0][0].toLowerCase()}ofs`;
      const ofsData = await fetchOfs(ofs);
      setTempLayers([
        {
          lake: 'primaryLake',
          sourceId: 'primaryLakeSource',
          data: ofsData,
        },
      ]);
    } else if (selectedLake.length === 0) {
      const [leofsData, lhofsData, lmofsData, loofsData, lsofsData] = await Promise.all([
        fetchOfs('leofs'),
        fetchOfs('lhofs'),
        fetchOfs('lmofs'),
        fetchOfs('loofs'),
        fetchOfs('lsofs'),
      ]);
      await setTempLayers([
        {
          lake: 'primaryLake',
          sourceId: 'primaryLakeSource',
          data: leofsData,
        },
        {
          lake: 'lhofs',
          sourceId: 'lhofsSource',
          data: lhofsData,
        },
        {
          lake: 'lmofs',
          sourceId: 'lmofsSource',
          data: lmofsData,
        },
        {
          lake: 'loofs',
          sourceId: 'loofsSource',
          data: loofsData,
        },
        {
          lake: 'lsofs',
          sourceId: 'lsofsSource',
          data: lsofsData,
        },
      ]);
    } setIsLoading(false);
  };

  useEffect(() => {
    if (!isLoading) {
      setIsLoading(true);
      fetchData(selectedLake)
        .then(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [selectedLake, UTCPath]);

  const tempStyle = {
    type: 'fill',
    beforeId: 'waterway-label',
    paint: {
      'fill-color':
        [
          'interpolate',
          [
            'cubic-bezier',
            1,
            1,
            0.4,
            0.7,
          ],
          ['get', 'level'],
          0,
          'hsl(268, 48%, 26%)',
          3.5,
          '#A35B80',
          6.5,
          '#1693A5',
          8.5,
          '#00d4b0',
          10.5,
          'hsl(88, 92%, 45%)',
          13.5,
          'hsl(73, 91%, 49%)',
          15.5,
          '#fffe0c',
          17.5,
          '#fbbe1a',
          19.5,
          brandingConfig.colors.accent1,
          21.5,
          '#ff0026',
          23.5,
          '#e81e76',
          25.5,
          '#ac0606',
          27.5,
          '#720202',
          29.5,
          '#4b0000',
        ],
      'fill-opacity': 1,
      'fill-antialias': true,
    },
  };
  const tempRange = isMobile
    ? [
      29.5,
      25.5,
      20.5,
      15.5,
      10.5,
      5.5,
      0,
    ]
    : [
      29.5,
      26.5,
      23.5,
      21.5,
      19.5,
      17.5,
      15.5,
      13.5,
      10.5,
      8.5,
      6.5,
      3.5,
      0,
    ];
  return (
    <>
      {isMobile
      && (
        <div className="mobile-temp-legend-container">
          <div className="mobile-temp-legend">
            {tempRange.map((tempRange, index) => (
              <div className="mobile-temp-legend-item" key={index}>
                <span className="mobile-temp-legend-item-text">
                  {preferredTemperatureUnit(tempRange)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {tempLayers.map((tempLayer, index) => (
        <Source
          id={tempLayer.sourceId}
          type="geojson"
          data={tempLayer.data}
          key={index}
        />
      ))}
      {tempLayers.map((tempLayer, index) => (
        <Layer
          id={tempLayer.lake}
          source={tempLayer.sourceId}
          {...tempStyle}
          key={index}
        />
      ))}
    </>
  );
};

SurfaceWaterTemperature.propTypes = {
  UTCPath: PropTypes.string,
  selectedLake: PropTypes.string,
  isSharing: PropTypes.bool,
};

SurfaceWaterTemperature.defaultProps = {
  UTCPath: '',
  selectedLake: '',
  isSharing: false,
};

export default SurfaceWaterTemperature;
